<template>
  <article class="home-product-card">
    <div v-if="product.images.length" class="home-product-card__media">
      <div
        class="home-product-card__media-row"
        :class="{ 'home-product-card__media-row--has-thumbs': thumbSlots.length }"
      >
        <button
          type="button"
          class="home-product-card__hero"
          @click="openGallery(0)"
        >
          <img
            :src="product.images[0]"
            :alt="product.name"
            class="home-product-card__hero-img"
          />
          <span v-if="formattedDate" class="home-product-card__date">
            <svg class="home-product-card__date-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1.5A2.5 2.5 0 0 1 22 6.5v13A2.5 2.5 0 0 1 19.5 22h-15A2.5 2.5 0 0 1 2 19.5v-13A2.5 2.5 0 0 1 4.5 4H6V3a1 1 0 0 1 1-1Zm12.5 7H4.5v10.5c0 .276.224.5.5.5h15a.5.5 0 0 0 .5-.5V9ZM6 6h-.5a.5.5 0 0 0-.5.5V7h14v-.5a.5.5 0 0 0-.5-.5H18v1a1 1 0 1 1-2 0V6H8v1a1 1 0 1 1-2 0V6Z"
              />
            </svg>
            {{ formattedDate }}
          </span>
        </button>

        <div
          v-if="thumbSlots.length"
          class="home-product-card__thumbs"
          :class="`home-product-card__thumbs--count-${Math.min(thumbSlots.length, 3)}`"
        >
          <button
            v-for="slot in thumbSlots"
            :key="`${product.id}-thumb-${slot.index}`"
            type="button"
            class="home-product-card__thumb"
            @click="openGallery(slot.index)"
          >
            <img :src="slot.src" :alt="`${product.name} image ${slot.index + 1}`" />
            <span v-if="slot.showMore" class="home-product-card__thumb-more">
              +{{ slot.moreCount }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <div class="home-product-card__header">
      <h3 class="home-product-card__name">{{ product.name }}</h3>
      <p v-if="product.subname" class="home-product-card__subname">{{ product.subname }}</p>
    </div>

    <div v-if="hasDescription" class="home-product-card__info-wrap">
      <div
        ref="descriptionEl"
        class="home-product-card__info"
        :class="{ 'is-expanded': descriptionExpanded }"
        v-html="product.description"
      />
      <button
        v-if="showReadMoreToggle"
        type="button"
        class="home-product-card__read-more"
        @click="toggleDescription"
      >
        {{ descriptionExpanded ? '[ read less ]' : '[ read more ]' }}
      </button>
    </div>

    <div v-if="hasFooterLinks" class="product-card-footer home-product-card__footer">
      <a
        v-if="product.youtube_primary_link"
        :href="product.youtube_primary_link"
        target="_blank"
        rel="noopener noreferrer"
        class="btn-youtube"
      >
        <img src="/wp-content/themes/rufousone/img/youtube-bttn-small.jpg" alt="Watch Review" />
      </a>

      <a
        v-if="hasAmazonLink"
        :href="product.amazon_url"
        target="_blank"
        rel="noopener noreferrer"
        class="buy-now-btn"
      >
        <img src="/wp-content/themes/rufousone/img/amazon-bttn.png" alt="Buy on Amazon" />
      </a>
    </div>

    <ImageGalleryModal
      :is-open="modalOpen"
      :images="product.images"
      :start-index="modalStartIndex"
      :product-name="product.name"
      @close="modalOpen = false"
    />
  </article>
</template>

<script>
import { defineComponent } from 'vue';
import ImageGalleryModal from './ImageGalleryModal.vue';

const MAX_THUMBS = 3;

export default defineComponent({
  name: 'HomeProductCard',
  components: {
    ImageGalleryModal
  },
  props: {
    product: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      modalOpen: false,
      modalStartIndex: 0,
      descriptionExpanded: false,
      descriptionOverflows: false
    };
  },
  computed: {
    hasDescription() {
      return Boolean(this.product.description && this.product.description.trim() !== '');
    },
    hasAmazonLink() {
      const url = (this.product.amazon_url || '').trim();
      return url !== '' && url !== '#';
    },
    hasFooterLinks() {
      return Boolean(this.product.youtube_primary_link || this.hasAmazonLink);
    },
    showReadMoreToggle() {
      return this.descriptionOverflows || this.descriptionExpanded;
    },
    formattedDate() {
      if (!this.product.date) {
        return '';
      }

      const parsed = new Date(this.product.date);
      if (Number.isNaN(parsed.getTime())) {
        return '';
      }

      return parsed.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    thumbSlots() {
      const images = this.product.images || [];

      if (images.length <= 1) {
        return [];
      }

      const extra = images.slice(1);

      if (extra.length <= MAX_THUMBS) {
        return extra.map((src, offset) => ({
          src,
          index: offset + 1,
          showMore: false,
          moreCount: 0
        }));
      }

      return [
        ...extra.slice(0, MAX_THUMBS - 1).map((src, offset) => ({
          src,
          index: offset + 1,
          showMore: false,
          moreCount: 0
        })),
        {
          src: extra[MAX_THUMBS - 1],
          index: MAX_THUMBS,
          showMore: true,
          moreCount: images.length - (1 + MAX_THUMBS)
        }
      ];
    }
  },
  watch: {
    'product.description'() {
      this.descriptionExpanded = false;
      this.checkDescriptionOverflow();
    }
  },
  mounted() {
    this.checkDescriptionOverflow();
  },
  updated() {
    this.checkDescriptionOverflow();
  },
  methods: {
    checkDescriptionOverflow() {
      this.$nextTick(() => {
        const el = this.$refs.descriptionEl;
        if (!el || this.descriptionExpanded) {
          return;
        }

        this.descriptionOverflows = el.scrollHeight > el.clientHeight + 1;
      });
    },
    openGallery(index) {
      this.modalStartIndex = index;
      this.modalOpen = true;
    },
    toggleDescription() {
      this.descriptionExpanded = !this.descriptionExpanded;

      if (!this.descriptionExpanded) {
        this.checkDescriptionOverflow();
      }
    }
  }
});
</script>
